import { settings } from "@/settings"
import { initFlowbite } from "flowbite"
import * as Iconoir from "iconoir-react"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { UpgradeModalContext, UserContext, VibratoClientContext } from "./contexts"
import PastDueModal from "./PastDueModal"
import { Haptics, ImpactStyle } from "@capacitor/haptics"
import { Drawer, DrawerItems, Sidebar, SidebarItemGroup, SidebarItems } from "flowbite-react"
import usertour from "usertour.js"
import TourTrigger from "@/components/TourTrigger"
import ReferralBanner from "./ReferralBanner"
import GiftSubscriptionBanner from "./GiftSubscriptionBanner"
import { ENABLE_REFERRALS, ENABLE_GIFTS } from "@/lib/feature_flags"
import ReferralModal from "./ReferralModal"
import GiftSubscriptionModal from "./GiftSubscriptionModal"
import UpgradeButton from "./UpgradeButton"

// // Helper function to determine if the nav tour should be shown
// function shouldShowNavTour(userSession: any, forceShow: boolean = false): boolean {
//   if (!userSession) return false

//   // If forced via URL param or manual trigger, always show
//   if (forceShow) return true

//   // Check if tour has been shown before in localStorage
//   const tourShownKey = `nav_tour_shown_${userSession.id}`
//   const tourShown = localStorage.getItem(tourShownKey)
//   if (tourShown === "true") {
//     return false
//   }

//   // Check if user was created more than a week ago
//   if (userSession.created_at) {
//     const userCreatedAt = new Date(userSession.created_at)
//     const oneWeekAgo = new Date()
//     oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

//     if (userCreatedAt < oneWeekAgo) {
//       return false
//     }
//   }

//   return true
// }

// Helper function to mark nav tour as shown
function markNavTourAsShown(userSession: any): void {
  if (userSession) {
    const tourShownKey = `nav_tour_shown_${userSession.id}`
    localStorage.setItem(tourShownKey, "true")
  }
}

// interface NavLink {
//   href: string
//   label: string
//   iconoir: string
// }

interface NavProps {
  mobileSize?: number
}

export default function Nav({ mobileSize }: NavProps): React.JSX.Element {
  return settings.SHOW_NATIVE_APP ? (
    <NavComponent isNative mobileSize={mobileSize} />
  ) : (
    <NavComponent isNative={false} mobileSize={mobileSize} />
  )
}

const desktopNavTourId = "cmcma43yp02vka3ms9o2qevhx"
const mobileNavTourId = "cmcm8t23f02p2o6e09j1wp83j"

function NavComponent({ isNative, mobileSize = 52 }: { isNative: boolean; mobileSize?: number }): React.JSX.Element {
  const user = useContext(UserContext)
  const upgradeModal = useContext(UpgradeModalContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [showPastDueModal, setShowPastDueModal] = useState(user.subscription_status == "past_due")
  const [showReferralModal, setShowReferralModal] = useState(false)
  const [showGiftModal, setShowGiftModal] = useState(false)
  const vibratoClient = useContext(VibratoClientContext)
  const [plan, setPlan] = useState<any>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Function to manually trigger the nav tour
  const triggerNavTour = useCallback(() => {
    console.log("Manually triggering nav tour")

    // This is a custom desktop-specific tour
    usertour.start(desktopNavTourId)
    console.log("Nav tour triggered")
    markNavTourAsShown(user)
  }, [user])

  // Function to manually trigger the nav tour
  const triggerMobileNavTour = useCallback(() => {
    console.log("Manually triggering mobile nav tour")
    usertour.start(mobileNavTourId)
    console.log("Mobile nav tour triggered")
    markNavTourAsShown(user)
  }, [user])

  // // Effect to trigger tour when mobile drawer opens
  // useEffect(() => {
  //   if (drawerOpen && isNative) {
  //     // Only show tour on mobile when drawer first opens
  //     setTimeout(() => {
  //       const forceTour = searchParams.get("nav_tour") === "true"
  //       if (shouldShowNavTour(user, forceTour)) {
  //         console.log("Starting nav tour on mobile drawer open")
  //         usertour.start(mobileNavTourId)
  //         markNavTourAsShown(user)
  //       } else {
  //         console.log("Nav tour skipped - already shown or user too old")
  //       }
  //     }, 500) // Small delay to let drawer animation complete
  //   }
  // }, [drawerOpen, isNative, user, searchParams])

  const hapticsImpactLight = async () => {
    if (isNative) {
      Haptics.impact({ style: ImpactStyle.Medium }).then(() => {
        console.log("Haptics triggered")
      })
    }
  }

  useEffect(() => {
    const fetchPlan = () => {
      vibratoClient
        .getPlan()
        .then((planData: any) => {
          setPlan(planData)
        })
        .catch((error: any) => {
          console.error("Error fetching plan data:", error)
        })
    }

    fetchPlan()
  }, [vibratoClient])

  useEffect(() => {
    initFlowbite()
  }, [])

  // Check for URL parameter to auto-open modal
  useEffect(() => {
    const giftParam = searchParams.get("gift")
    if (giftParam === "1" && user) {
      if (user.plan === "freemium") {
        setShowReferralModal(true)
      } else {
        setShowGiftModal(true)
      }
      // Clean up URL parameter
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete("gift")
      setSearchParams(newSearchParams, { replace: true })
    }
  }, [searchParams, user, setSearchParams])

  const toggleUpgradeModal = () => {
    upgradeModal.setShowModal(true)
    if (["pro", "scale", "enterprise"].includes(user.plan)) {
      upgradeModal.setInitialView("business")
    } else {
      upgradeModal.setInitialView("individual")
    }
  }

  const upgradeablePlans = ["freemium", "plus", "pro", "lite", "standard"]
  const showSubscribe = upgradeablePlans.includes(user.plan)

  const referralMessage = (): React.ReactNode => {
    if (user.plan === "freemium" && ENABLE_REFERRALS) {
      return <ReferralBanner />
    } else if (["plus", "lite", "standard", "pro", "scale", "enterprise"].includes(user.plan) && ENABLE_GIFTS) {
      return <GiftSubscriptionBanner />
    }
    return null
  }

  // Logo logic
  const squareLogoPath = "/vibrato-logo-foreground.png"
  let logoPath = "/vibrato-biz-logo.svg"
  if (user.plan == "plus") {
    logoPath = "/vibrato-plus-logo.svg"
  }
  if (user.plan == "lite") {
    logoPath = "/vibrato-lite-logo.svg"
  }
  if (user.plan == "standard") {
    logoPath = "/vibrato-standard-logo.svg"
  }
  if (user.plan == "freemium") {
    logoPath = "/vibrato-logo.svg"
  }

  // Define links based on whether it's native or desktop
  const getTopLinks = () => {
    interface NavLink {
      id: string
      href: string
      label: React.ReactNode
      iconoir?: keyof typeof Iconoir
      onClick?: () => void
      show?: boolean
      color?: string
      target?: string
      rel?: string
    }

    const commonLinks: NavLink[] = [
      ...(user.plan === "freemium"
        ? [
            {
              id: "nav-upgrade-freemium",
              href: "#",
              label: <UpgradeButton onClick={toggleUpgradeModal} plan={plan} />,
              // iconoir: "Crown" as keyof typeof Iconoir,
              onClick: toggleUpgradeModal,
              show: showSubscribe,
              color: "text-purple-600"
            }
          ]
        : []),
      {
        id: "nav-dashboard",
        href: "/dashboard",
        label: "Dashboard",
        iconoir: "DashboardDots" as keyof typeof Iconoir,
        target: "",
        rel: ""
      },
      {
        id: "nav-call-library",
        href: "/call-library",
        label: "Call Library",
        iconoir: "Hammer" as keyof typeof Iconoir
      },
      { id: "nav-design-call", href: "/chat", label: "Design a Call", iconoir: "ChatBubble" as keyof typeof Iconoir },
      {
        id: "nav-task-templates",
        href: "/task-templates",
        label: "Task Templates",
        iconoir: "Copy" as keyof typeof Iconoir
      },
      {
        id: "nav-phone-numbers",
        href: "/phone-numbers",
        label: "Phone Numbers",
        iconoir: "Phone" as keyof typeof Iconoir
      },
      {
        id: "nav-call-history",
        href: "/calls",
        label: "Call History",
        iconoir: "MultiplePages" as keyof typeof Iconoir
      },
      {
        id: "nav-business-tools",
        href: "/business",
        label: "Business Tools",
        iconoir: "Building" as keyof typeof Iconoir
      }
    ]

    // Add desktop-only links
    if (!isNative) {
      return [
        ...commonLinks,
        { id: "nav-contacts", href: "/contacts", label: "Contacts", iconoir: "Group" as keyof typeof Iconoir },
        { id: "nav-campaigns", href: "/campaigns", label: "Campaigns", iconoir: "Send" as keyof typeof Iconoir },
        {
          id: "nav-integrations",
          href: "/integrations",
          label: "Integrations",
          iconoir: "Server" as keyof typeof Iconoir
        },
        { id: "nav-api-keys", href: "/api-keys", label: "API", iconoir: "Network" as keyof typeof Iconoir }
      ]
    }

    return commonLinks
  }

  const getBottomLinks = () => {
    interface NavLink {
      id: string
      href: string
      label: React.ReactNode
      iconoir?: keyof typeof Iconoir
      onClick?: () => void
      show?: boolean
      color?: string
      target?: string
      rel?: string
    }

    var commonLinks: NavLink[] = [
      ...(user.plan !== "freemium"
        ? [
            {
              id: "nav-upgrade-paid",
              href: "#",
              label: <UpgradeButton onClick={toggleUpgradeModal} plan={plan} />,
              onClick: toggleUpgradeModal,
              show: showSubscribe,
              color: "text-purple-600",
              target: "_self",
              rel: ""
            }
          ]
        : []),
      {
        id: "nav-settings",
        href: "/settings/profile",
        label: "Settings",
        iconoir: "Settings" as keyof typeof Iconoir,
        show: true,
        target: "_self",
        rel: ""
      }
    ]

    // Add desktop-only links to the end of commonLinks
    if (!isNative) {
      commonLinks.push(
        {
          id: "nav-mobile-app",
          href: "/mobile-app",
          label: "Get Mobile App",
          iconoir: "Download" as keyof typeof Iconoir,
          show: true,
          target: "_self",
          rel: ""
        },
        {
          id: "nav-discord",
          href: "https://discord.gg/3Cpz55YqmD",
          label: "Join our Discord",
          iconoir: "Discord" as keyof typeof Iconoir,
          show: true,
          target: "_blank",
          rel: "noopener noreferrer"
        },
        {
          id: "nav-email",
          href: "mailto:contact@getvibrato.com",
          label: "Email Us",
          iconoir: "MailOut" as keyof typeof Iconoir,
          show: true
        }
      )
    }

    commonLinks.push({
      id: "nav-logout",
      href: "/logout",
      label: "Log Out",
      iconoir: "LogOut" as keyof typeof Iconoir,
      show: true,
      target: "_self",
      rel: ""
    })

    return commonLinks
  }

  const topLinks = getTopLinks().filter((link) => link.label != "")
  const bottomLinks = getBottomLinks()
  const showPlaceCallButton = true

  // Custom sidebar item component
  const CustomSidebarItem = ({
    id,
    href,
    label,
    iconoir,
    onClick,
    target,
    rel
  }: {
    id: string
    href: string
    label: React.ReactNode
    iconoir?: keyof typeof Iconoir
    onClick?: () => void
    target?: string
    rel?: string
  }) => {
    const handleClick = async (e: React.MouseEvent) => {
      await hapticsImpactLight()

      // Handle external links
      if (target === "_blank") {
        e.preventDefault()
        window.open(href, "_blank", rel)
        return
      }

      // Handle custom onClick
      if (onClick) {
        e.preventDefault()
        onClick()
      }
    }

    // Render different icons based on the iconoir prop
    let icon
    switch (iconoir) {
      case "Crown":
        icon = <Iconoir.Crown className="w-6 h-6 text-gray-500" />
        break
      case "DashboardDots":
        icon = <Iconoir.DashboardDots className="w-6 h-6 text-gray-500" />
        break
      case "Hammer":
        icon = <Iconoir.Hammer className="w-6 h-6 text-gray-500" />
        break
      case "ChatBubble":
        icon = <Iconoir.ChatBubble className="w-6 h-6 text-gray-500" />
        break
      case "Copy":
        icon = <Iconoir.Copy className="w-6 h-6 text-gray-500" />
        break
      case "Phone":
        icon = <Iconoir.Phone className="w-6 h-6 text-gray-500" />
        break
      case "Building":
        icon = <Iconoir.Building className="w-6 h-6 text-gray-500" />
        break
      case "MultiplePages":
        icon = <Iconoir.MultiplePages className="w-6 h-6 text-gray-500" />
        break
      case "Settings":
        icon = <Iconoir.Settings className="w-6 h-6 text-gray-500" />
        break
      case "Group":
        icon = <Iconoir.Group className="w-6 h-6 text-gray-500" />
        break
      case "Send":
        icon = <Iconoir.Send className="w-6 h-6 text-gray-500" />
        break
      case "Server":
        icon = <Iconoir.Server className="w-6 h-6 text-gray-500" />
        break
      case "Network":
        icon = <Iconoir.Network className="w-6 h-6 text-gray-500" />
        break
      case "BookStack":
        icon = <Iconoir.BookStack className="w-6 h-6 text-gray-500" />
        break
      case "CreditCard":
        icon = <Iconoir.CreditCard className="w-6 h-6 text-gray-500" />
        break
      case "LogOut":
        icon = <Iconoir.LogOut className="w-6 h-6 text-gray-500" />
        break
      case "Discord":
        icon = <Iconoir.Discord className="w-6 h-6 text-gray-500" />
        break
      case "MailOut":
        icon = <Iconoir.MailOut className="w-6 h-6 text-gray-500" />
        break
      case "ProfileCircle":
        icon = <Iconoir.ProfileCircle className="w-6 h-6 text-gray-500" />
        break
      case "Download":
        icon = <Iconoir.Download className="w-6 h-6 text-gray-500" />
        break
      // default:
      //   icon = <Iconoir.ChatBubble className="w-6 h-6 text-gray-500" />
    }

    return (
      <li id={id} className="flex items-center p-2 text-base font-normal rounded-lg hover:bg-gray-100">
        <Link to={href} onClick={handleClick} className="flex items-center w-full">
          {iconoir && (
            <>
              {icon}
              <span className="ms-3 flex-grow">{label}</span>
            </>
          )}
          {!iconoir && <span className="flex-grow">{label}</span>}
        </Link>
      </li>
    )
  }

  // Create a UserEmailDisplay component
  const UserEmailDisplay = () => {
    return (
      <div className="px-3 py-3 mb-3 border-t border-gray-200">
        <div className="flex items-center">
          <Iconoir.User className="w-5 h-5 text-gray-400 mr-2" />
          <div className="flex flex-col">
            <span className="text-xs font-medium text-gray-400">Logged in as</span>
            <span className="text-sm font-medium text-gray-600 truncate max-w-[190px]">{user.email}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <PastDueModal showModal={showPastDueModal} setShowModal={setShowPastDueModal} />
      <ReferralModal show={showReferralModal} onClose={() => setShowReferralModal(false)} />
      <GiftSubscriptionModal show={showGiftModal} onClose={() => setShowGiftModal(false)} />

      {/* Mobile top bar (non-fixed) */}
      <div className="z-50 bg-white border-b border-gray-200 lg:hidden flex flex-col">
        {/* This div contains the actual navigation elements with customizable height */}
        <div className="w-full flex items-center justify-between px-3" style={{ height: `${mobileSize}px` }}>
          <button
            type="button"
            className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
            onClick={() => {
              hapticsImpactLight()
              setDrawerOpen(!drawerOpen)
            }}
          >
            <span className="sr-only">Open sidebar</span>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clipRule="evenodd"
                fillRule="evenodd"
                d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
              />
            </svg>
          </button>
          <div className="flex items-center justify-center absolute left-0 right-0 mx-auto pointer-events-none">
            <img src={squareLogoPath} className="h-8 w-8" alt="Vibrato Logo" />
          </div>
          <div className="w-10" /> {/* Spacer to balance the hamburger menu */}
        </div>
      </div>

      {/* Flowbite Drawer for mobile */}
      <Drawer
        backdrop={true}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          hapticsImpactLight()
        }}
        id="mobile-sidebar"
        className="lg:hidden bg-gray-50 p-0 w-min h-fit"
      >
        {/* <DrawerHeader title="" titleIcon={() => <></>} /> */}
        <DrawerItems className="bg-white h-full min-h-fit">
          <Sidebar id="logo-sidebar" className="pt-safe pb-safe h-[100dvh] overflow-y-auto bg-gray-50 px-0 py-0 w-60">
            <SidebarItems className="flex flex-col bg-gray-50 px-0 py-0 h-full">
              <div className="flex items-center justify-between ps-2.5 mb-4 pt-4 pr-2">
                <img src={logoPath} className="h-6 me-3" alt="Vibrato Logo" />
                <TourTrigger
                  onClick={() => {
                    triggerMobileNavTour()
                  }}
                  tooltipContent="Take a navigation tour"
                />
              </div>
              {/* Top Links */}
              <SidebarItemGroup className="space-y-2 font-medium mt-0 pt-0 border-t-0 px-0 py-0 pb-4">
                {topLinks.map(({ id, href, label, iconoir, onClick, target, rel }) => (
                  <CustomSidebarItem
                    key={id}
                    id={id}
                    href={href}
                    label={label}
                    iconoir={iconoir}
                    onClick={() => {
                      if (onClick) onClick()
                      setDrawerOpen(false)
                    }}
                    target={target}
                    rel={rel}
                  />
                ))}
              </SidebarItemGroup>

              {/* Bottom Links Group */}
              <div className="flex flex-col grow justify-end">
                <div className="mb-4">{referralMessage()}</div>
                <SidebarItemGroup className="space-y-3">
                  {showPlaceCallButton && (
                    <div className="mb-4">
                      <Link
                        to="/new-call"
                        onClick={() => {
                          hapticsImpactLight()
                          setDrawerOpen(false)
                        }}
                      >
                        <div className="w-full px-4 py-3 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200">
                          <Iconoir.Phone className="w-5 h-5" />
                          <span className="font-semibold">Place a Call</span>
                        </div>
                      </Link>
                    </div>
                  )}

                  {/* Add User Email Display above the bottom links */}
                  <div className="mb-3">
                    <UserEmailDisplay />
                  </div>

                  {bottomLinks.map(({ id, href, label, iconoir, onClick, show, target, rel }) =>
                    show ? (
                      <CustomSidebarItem
                        key={id}
                        id={id}
                        href={href}
                        label={label}
                        iconoir={iconoir}
                        onClick={() => {
                          if (onClick) onClick()
                          setDrawerOpen(false)
                        }}
                        target={target}
                        rel={rel}
                      />
                    ) : null
                  )}
                </SidebarItemGroup>
              </div>
            </SidebarItems>
          </Sidebar>
        </DrawerItems>
      </Drawer>

      {/* Desktop Sidebar using Flowbite React - keep the original sidebar for desktop */}
      <aside
        id="logo-sidebar"
        className="fixed left-0 z-40 w-64 h-screen transition-transform -translate-x-full lg:translate-x-0 lg:pt-0 hidden lg:block"
        aria-label="Sidebar"
      >
        <Sidebar id="logo-sidebar" className="flex flex-col h-full bg-gray-50 overflow-y-auto pt-4 lg:pt-0">
          {/* Logo for Desktop Sidebar */}

          <div className="flex flex-col h-full">
            <div className="hidden lg:flex items-center justify-between ps-2.5 mb-4">
              <img src={logoPath} className="h-6 me-3 lg:h-7" alt="Vibrato Logo" />
              <TourTrigger onClick={triggerNavTour} tooltipContent="Take a navigation tour" />
            </div>

            <SidebarItems className="flex flex-col grow">
              {/* Top Links */}
              <SidebarItemGroup className="space-y-2 font-medium pb-4 grow">
                {topLinks.map(({ id, href, label, iconoir, onClick, target, rel }) => (
                  <CustomSidebarItem
                    key={`desktop-${id}`}
                    id={`desktop-${id}`}
                    href={href}
                    label={label}
                    iconoir={iconoir}
                    onClick={onClick}
                    target={target}
                    rel={rel}
                  />
                ))}
              </SidebarItemGroup>

              {/* SOC 2 Protected Banner - Only show on desktop */}
              {/* {!isNative && (
                <div className="px-2 py-2 mb-4 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center py-2">
                    <Iconoir.ShieldCheck className="w-7 h-7 text-purple-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">SOC 2 Protected</p>
                      <p className="text-[10px] text-gray-600">Industry-leading security standards</p>
                    </div>
                  </div>
                </div>
              )} */}

              {/* Bottom Links Group */}
              <SidebarItemGroup className="space-y-3">
                <div className="mb-4">{referralMessage()}</div>
                {showPlaceCallButton && (
                  <div className="mb-4">
                    <Link to="/new-call" onClick={hapticsImpactLight}>
                      <div className="w-full px-4 py-3 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200">
                        <Iconoir.Phone className="w-5 h-5" />
                        <span className="font-semibold">Place a Call</span>
                      </div>
                    </Link>
                  </div>
                )}

                {/* Add User Email Display above the bottom links */}
                <div className="mb-3">
                  <UserEmailDisplay />
                </div>

                {bottomLinks.map(({ id, href, label, iconoir, onClick, show, target, rel }) =>
                  show ? (
                    <CustomSidebarItem
                      key={`desktop-${id}`}
                      id={`desktop-${id}`}
                      href={href}
                      label={label}
                      iconoir={iconoir}
                      onClick={onClick}
                      target={target}
                      rel={rel}
                    />
                  ) : null
                )}
              </SidebarItemGroup>
            </SidebarItems>
          </div>
        </Sidebar>
      </aside>
    </div>
  )
}
